import { QueryClient, QueryFunction, QueryKey, dehydrate } from '@tanstack/react-query';
import { SSRConfig } from 'next-i18next';

import { PAGE_REVALIDATE_TIME } from '@/utils/constants';

import { MetadataFragment } from '../generated/graphql';

type QueryValue<T> = T extends Query<infer TData> ? TData : never;
type QueryVariables<T> = T extends Query<unknown, infer TVariables> ? TVariables : never;

interface Query<TData = unknown, TVariables = unknown> {
  getKey(variables?: TVariables): QueryKey;
  fetcher(variables?: TVariables): QueryFunction<TData>;
}

type PrefetchedQuery<TQuery extends Query = Query> = {
  query: TQuery;
  variables?: QueryVariables<TQuery>;
  result: Promise<QueryValue<TQuery>>;
};

type WithQueryOptions<TQuery extends Query = Query> = {
  variables?: QueryVariables<TQuery>;
  getMetadata?: (result: QueryValue<TQuery>) => MetadataFragment | undefined;
  isPrivate?: boolean;
};

const publicClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: PAGE_REVALIDATE_TIME * 1000,
    },
  },
});

export class PropsBuilder {
  queries: PrefetchedQuery[] = [];
  privateClient = new QueryClient();
  metadata: MetadataFragment | undefined;

  private async publicFetch<TQuery extends Query>(
    query: TQuery,
    variables?: QueryVariables<TQuery>,
  ): Promise<QueryValue<TQuery>> {
    const publicResult = (await publicClient.fetchQuery(
      query.getKey(variables),
      query.fetcher(variables),
    )) as QueryValue<TQuery>;

    this.privateClient.setQueryData(query.getKey(variables), publicResult);

    return publicResult;
  }

  private privateFetch<TQuery extends Query>(
    query: TQuery,
    variables?: QueryVariables<TQuery>,
  ): Promise<QueryValue<TQuery>> {
    return this.privateClient.fetchQuery(query.getKey(variables), query.fetcher(variables));
  }

  withQuery<TQuery extends Query>(query: TQuery, options: WithQueryOptions<TQuery> = {}) {
    const { getMetadata, isPrivate, variables } = options;

    let result = isPrivate ? this.privateFetch(query, variables) : this.publicFetch(query, variables);

    if (getMetadata) {
      result = result.then((result) => {
        this.metadata = getMetadata(result);
        return result;
      });
    }

    this.queries.push({ query, variables, result });

    return result;
  }

  async build(langConfig?: SSRConfig) {
    await Promise.all(this.queries.map(({ result }) => result));

    return {
      dehydratedState: dehydrate(this.privateClient),
      ...langConfig,
      ...(this.metadata && { metadata: this.metadata }),
    };
  }
}
